import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { AlertTriangle, Check, Sparkles, X } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.jsx';
import Tooltip from './Tooltip';
import FormCheckSuggestionModal from './FormCheckSuggestionModal';
import {
  FORM_CHECK_COLOR,
  formCheckIssueMessages,
  issuesSeverity,
  resolveFormCheckState,
} from '../utils/formCheckDisplay';

const WARN_COLOR = FORM_CHECK_COLOR;
const OK_COLOR = '#22c55e';

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const BorderWrap = styled.div`
  position: relative;
  display: ${p => (p.$fill ? 'block' : 'inline-block')};
  width: ${p => (p.$fill ? '100%' : 'auto')};
  border-radius: 12px;
  box-sizing: border-box;
`;

const BadgeRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  vertical-align: middle;
`;

const ShimmerBar = styled.div`
  height: ${p => (p.$size === 'sm' ? '4px' : '6px')};
  border-radius: 3px;
  background: linear-gradient(
    90deg,
    rgba(168, 85, 247, 0.1) 0%,
    rgba(168, 85, 247, 0.35) 50%,
    rgba(168, 85, 247, 0.1) 100%
  );
  background-size: 200px 100%;
  background-color: rgba(168, 85, 247, 0.12);

  @media (prefers-reduced-motion: no-preference) {
    animation: ${shimmer} 1.5s infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: rgba(168, 85, 247, 0.18);
  }
`;

const PendingSlot = styled.div`
  ${p => (p.$overlay ? `
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 8px;
    pointer-events: none;
  ` : 'width: 72px;')}
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: ${p => (p.$size === 'sm' ? '18px' : '22px')};
  height: ${p => (p.$size === 'sm' ? '18px' : '22px')};
  border-radius: 999px;
  font-size: ${p => (p.$size === 'sm' ? '11px' : '13px')};
  font-weight: 800;
  line-height: 1;
  flex-shrink: 0;
  background: ${p => (p.$warn ? `${WARN_COLOR}22` : `${OK_COLOR}22`)};
  color: ${p => (p.$warn ? WARN_COLOR : OK_COLOR)};
  border: 1px solid ${p => (p.$warn ? `${WARN_COLOR}88` : `${OK_COLOR}88`)};
`;

const WarnCluster = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${p => (p.$overlay ? `
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
  ` : '')}
`;

const DismissBtn = styled.button`
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover { background: rgba(255, 255, 255, 0.18); color: #fff; }
`;

const BorderPendingBar = styled.div`
  margin-top: 8px;
  padding: 0 2px;
`;

const BorderOkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.78rem;
  color: ${OK_COLOR};
`;

const WarnPanel = styled.div`
  margin-top: ${p => (p.$placement === 'above' ? '0' : '10px')};
  margin-bottom: ${p => (p.$placement === 'above' ? '10px' : '0')};
  padding: 12px 14px;
  border-radius: 10px;
  background: ${WARN_COLOR}14;
  border: 1px solid ${WARN_COLOR}44;
`;

const IssueText = styled.p`
  margin: 0 0 8px;
  font-size: 0.85rem;
  line-height: 1.45;
  color: ${WARN_COLOR};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SuggestionLabel = styled.div`
  margin: 10px 0 6px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const SuggestionText = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.85rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.95);
  white-space: pre-wrap;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const PanelBtn = styled.button`
  min-height: 44px;
  min-width: 44px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const AcceptBtn = styled(PanelBtn)`
  background: ${OK_COLOR};
  color: #0f172a;
  border-color: ${OK_COLOR};

  &:hover { filter: brightness(1.05); }
`;

const DismissPanelBtn = styled(PanelBtn)`
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`;

const PillRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: ${p => (p.$placement === 'above' ? '0' : '8px')};
  margin-bottom: ${p => (p.$placement === 'above' ? '8px' : '0')};
`;

/** The quiet form of a warning: one tappable pill that opens the note in a modal. */
const NotePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 4px 12px 4px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  color: ${p => p.$color};
  background: ${p => p.$color}1f;
  border: 1px solid ${p => p.$color}66;

  &:hover { background: ${p => p.$color}33; }
`;

/** variant="pill": the warning as a NotePill; the note and suggestion wait in a modal. */
function PillWarn({ issues, suggestedText, onAcceptSuggestion, onDismiss, placement, t }) {
  const [open, setOpen] = useState(false);
  const severity = issuesSeverity(issues);
  const Icon = severity === 'blocking' ? AlertTriangle : Sparkles;
  return (
    <PillRow $placement={placement}>
      <NotePill
        type="button"
        $color={WARN_COLOR}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Icon size={14} />
        {t(`formCheck.pill.${severity}`)}
      </NotePill>
      {onDismiss && (
        <DismissBtn type="button" onClick={(e) => { e.stopPropagation(); onDismiss(); }} aria-label={t('formCheck.dismiss')}>
          <X size={11} />
        </DismissBtn>
      )}
      <FormCheckSuggestionModal
        open={open}
        severity={severity}
        issues={issues}
        suggestedText={suggestedText}
        onAcceptSuggestion={onAcceptSuggestion}
        onDismiss={onDismiss}
        onClose={() => setOpen(false)}
      />
    </PillRow>
  );
}

function StatusMark({ state, size, issues, onDismiss, overlay, t }) {
  if (state === 'pending') {
    return (
      <PendingSlot $overlay={overlay} aria-busy="true" aria-label={t('formCheck.a11y.pending')}>
        <ShimmerBar $size={size} />
      </PendingSlot>
    );
  }

  if (state === 'ok') {
    return (
      <WarnCluster $overlay={overlay} aria-label={t('formCheck.a11y.ok')}>
        <Pill $size={size} title={t('formCheck.okLabel')}>
          <Check size={size === 'sm' ? 11 : 13} strokeWidth={3} />
        </Pill>
      </WarnCluster>
    );
  }

  if (state === 'warn') {
    const tooltipText = issues.map((issue) => issue.message).filter(Boolean).join('\n');
    return (
      <WarnCluster $overlay={overlay} aria-label={t('formCheck.a11y.warn')}>
        <Tooltip text={tooltipText || t('formCheck.warnLabel')} trigger="hover" maxWidth="260px" placement="top">
          <Pill $warn $size={size} style={{ color: WARN_COLOR, cursor: 'help' }}>!</Pill>
        </Tooltip>
        {onDismiss && (
          <DismissBtn type="button" onClick={(e) => { e.stopPropagation(); onDismiss(); }} aria-label={t('formCheck.dismiss')}>
            <X size={11} />
          </DismissBtn>
        )}
      </WarnCluster>
    );
  }

  return null;
}

function BorderWarnPanel({
  issues,
  suggestedText,
  onAcceptSuggestion,
  onDismiss,
  placement = 'below',
  t,
}) {
  return (
    <WarnPanel $placement={placement} role="alert" aria-live="polite">
      {issues.map((issue, index) => (
        <IssueText key={issue.code || index}>{issue.message}</IssueText>
      ))}
      {suggestedText && (
        <>
          <SuggestionLabel>{t('formCheck.suggestedLabel')}</SuggestionLabel>
          <SuggestionText>{suggestedText}</SuggestionText>
        </>
      )}
      {(onAcceptSuggestion && suggestedText) || onDismiss ? (
        <ActionRow>
          {suggestedText && onAcceptSuggestion && (
            <AcceptBtn
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAcceptSuggestion(suggestedText);
              }}
            >
              {t('formCheck.acceptSuggestion')}
            </AcceptBtn>
          )}
          {onDismiss && (
            <DismissPanelBtn
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
            >
              {t('formCheck.dismiss')}
            </DismissPanelBtn>
          )}
        </ActionRow>
      ) : null}
    </WarnPanel>
  );
}

export default function InputFeedbackIndicator({
  state,
  issues = [],
  suggestedText = null,
  variant = 'badge',
  size = 'md',
  autoHideOkMs = 4000,
  onDismiss,
  onAcceptSuggestion,
  fill = false,
  /** For variant="border" | "pill": render the warning above or below children. */
  warnPlacement = 'below',
  children,
}) {
  const { t } = useTranslation();
  const [okHidden, setOkHidden] = useState(false);

  useEffect(() => {
    if (state !== 'ok') {
      setOkHidden(false);
      return undefined;
    }
    if (!autoHideOkMs) return undefined;
    const timer = setTimeout(() => setOkHidden(true), autoHideOkMs);
    return () => clearTimeout(timer);
  }, [state, autoHideOkMs]);

  if (state !== 'pending' && state !== 'ok' && state !== 'warn') return children || null;
  if (state === 'ok' && okHidden) return children || null;

  if (variant === 'border' || variant === 'pill') {
    const placement = warnPlacement === 'above' ? 'above' : 'below';
    const WarnView = variant === 'pill' ? PillWarn : BorderWarnPanel;
    const warnPanel = state === 'warn' ? (
      <WarnView
        issues={issues}
        suggestedText={suggestedText}
        onAcceptSuggestion={onAcceptSuggestion}
        onDismiss={onDismiss}
        placement={placement}
        t={t}
      />
    ) : null;
    const panelAbove = placement === 'above';

    return (
      <BorderWrap $fill={fill}>
        {panelAbove && warnPanel}
        {children}
        {state === 'pending' && (
          <BorderPendingBar aria-busy="true" aria-label={t('formCheck.a11y.pending')}>
            <ShimmerBar $size={size} />
          </BorderPendingBar>
        )}
        {state === 'ok' && !okHidden && (
          <BorderOkRow aria-label={t('formCheck.a11y.ok')}>
            <Pill $size={size} title={t('formCheck.okLabel')}>
              <Check size={size === 'sm' ? 11 : 13} strokeWidth={3} />
            </Pill>
            {t('formCheck.okLabel')}
          </BorderOkRow>
        )}
        {!panelAbove && warnPanel}
      </BorderWrap>
    );
  }

  const mark = (
    <StatusMark
      state={state}
      size={size}
      issues={issues}
      onDismiss={state === 'warn' ? onDismiss : undefined}
      overlay={false}
      t={t}
    />
  );

  if (children) {
    return (
      <BadgeRow>
        {children}
        {mark}
      </BadgeRow>
    );
  }

  return mark;
}

/**
 * Own-record gate + state mapping for the three workshop surfaces.
 */
export function FormCheckOwnIndicator({
  isOwn,
  formCheck,
  pending = false,
  dismissed = false,
  onDismiss,
  onAcceptSuggestion,
  variant = 'badge',
  size = 'md',
  fill = false,
  warnPlacement = 'below',
  children,
}) {
  const { t } = useTranslation();
  if (!isOwn) return children || null;
  const resolved = resolveFormCheckState({ formCheck, pending });
  const state = dismissed && resolved === 'warn' ? 'none' : resolved;
  if (state === 'none') return children || null;
  const suggestion = formCheck?.suggestedText ? String(formCheck.suggestedText).trim() : null;
  return (
    <InputFeedbackIndicator
      state={state}
      issues={formCheckIssueMessages(formCheck, t)}
      suggestedText={suggestion || null}
      variant={variant}
      size={size}
      fill={fill}
      warnPlacement={warnPlacement}
      onDismiss={onDismiss}
      onAcceptSuggestion={onAcceptSuggestion}
    >
      {children}
    </InputFeedbackIndicator>
  );
}

export { WARN_COLOR, OK_COLOR };
